import { ModalProps } from "antd";
import React from "react";
import { connect } from "react-redux";
import styled from "styled-components";

import { COLORS } from "../constants";
import { AppState } from "../store";
import { submitCloudTablesOAuthForm, submitWebDashboardGoogleOAuthForm } from "../utils";

import { Button } from "./Button";
import { ConnectCoeffIcon, ConnectPlugIcon, GoogleDrive, GoogleSheetsIcon, Lock } from "./Icons";
import { message } from "./message";
import { Modal } from "./Modal";
import { Typography } from "./Typography";

const SheetsModal = styled(Modal)`
  .ant-modal-content {
    border-radius: 8px;
    box-shadow: 0px 0px 16px 0px ${COLORS.black8}, 0px 16px 24px 0px ${COLORS.black8};
  }

  .ant-modal-close {
    top: 17px;
    right: 20px;
  }

  .ant-modal-close-x {
    width: 12px;
    height: 12px;
    line-height: 12px;
  }
`;

const SheetsContent = styled.div`
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 20px;
`;

// Match Figma layout: connect icon at (0,5) sized ~75x50,
// sheets icon at (87,0) sized 60x60.
const SheetsHeaderIcons = styled.div`
  position: relative;
  width: 147px;
  height: 60px;
`;

const SheetsConnectIconWrap = styled.div`
  position: absolute;
  left: 0;
  top: 5px;
  width: 75px;
  height: 50px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const SheetsIconCircle = styled.div`
  position: absolute;
  left: 87px;
  top: 0;
  width: 60px;
  height: 60px;
  border-radius: 9999px;
  background: ${COLORS.greenBg5};
  display: flex;
  align-items: center;
  justify-content: center;
`;

const DriveContent = styled.div`
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: space-beween;
  gap: 40px;
  padding-top: 32px;
`;

const IconRow = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const ContentText = styled.div`
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
`;

const AccountCallout = styled.div`
  width: 100%;
  background: ${COLORS.coeblue11};
  border-radius: 4px;
  padding: 12px 40px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
`;

const SheetsFooter = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  line-height: 1.3;
  margin-top: 4px;
  width: 100%;
  text-align: left;
`;

const SheetsAuthorizeButton = styled(Button)`
  width: 100%;
  height: 32px;
  border-radius: 4px;
  font-size: 14px;
  line-height: 18px;
`;

type ConnectProps = {
  email: string;
};

export type GoogleOAuthErrorReason = "token_expired" | "admin_restricted";

type Props = ModalProps &
  ConnectProps & {
    redirectPath: string;
    mode?: "drive" | "sheets";
    webDashboardId?: string;
    // "admin_restricted": Google refused the stored grant (access_denied), so reconnecting only
    // works once the user's Workspace admin allows Coefficient again (ENG-8597).
    reason?: GoogleOAuthErrorReason;
  };

const GoogleAuthModalBase: React.FC<Props> = ({
  email,
  redirectPath,
  mode = "drive",
  webDashboardId,
  reason = "token_expired",
  ...rest
}) => {
  const [isLoading, setIsLoading] = React.useState(false);

  const handleAuthorizeClick = () => {
    try {
      setIsLoading(true);

      if (mode === "sheets") {
        submitWebDashboardGoogleOAuthForm({ email, redirectPath, webDashboardId });
      } else {
        submitCloudTablesOAuthForm({ email, redirectPath });
      }
    } catch (error) {
      message.error("Failed to authorize");
    } finally {
      setIsLoading(false);
    }
  };

  return mode === "sheets" ? (
    <SheetsModal {...rest} padding="60px 20px 20px" width={400}>
      <SheetsContent>
        <SheetsHeaderIcons>
          <SheetsConnectIconWrap>
            <ConnectCoeffIcon width="75" height="50" />
          </SheetsConnectIconWrap>
          <SheetsIconCircle>
            <GoogleSheetsIcon width="32" height="32" />
          </SheetsIconCircle>
        </SheetsHeaderIcons>

        <ContentText>
          <Typography fontSize="18px" lineHeight="22px" fontWeight={700}>
            Reconnect Google Sheets
          </Typography>

          {reason === "admin_restricted" ? (
            <>
              <Typography fontSize="14px" lineHeight="18px" fontWeight={500} color="textSecondary">
                Google has stopped allowing Coefficient to access your account. This usually happens
                when your Google Workspace admin changes app or security settings.
              </Typography>
              <Typography fontSize="14px" lineHeight="18px" fontWeight={500} color="textSecondary">
                Try reconnecting below. If Google blocks the request, ask your admin to allow
                Coefficient in the Google Admin console, then reconnect.
              </Typography>
            </>
          ) : (
            <Typography fontSize="14px" lineHeight="18px" fontWeight={500} color="textSecondary">
              You need to reauthorize Coefficient to
              <br />
              connect to Google Sheets.
            </Typography>
          )}
        </ContentText>

        <AccountCallout>
          <Typography fontSize="14px" lineHeight="18px" fontWeight={500} color="textPrimary">
            Make sure to reconnect using the same Google Sheets account
          </Typography>
          <Typography fontSize="14px" lineHeight="18px" fontWeight={700}>
            {email}
          </Typography>
        </AccountCallout>

        <SheetsAuthorizeButton
          disabled={isLoading}
          block
          type="primary"
          onClick={() => handleAuthorizeClick()}
        >
          Authorize
        </SheetsAuthorizeButton>

        <SheetsFooter>
          <div
            style={{
              width: 32,
              height: 32,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Lock fill={COLORS.black} />
          </div>

          <Typography
            fontSize="12px"
            lineHeight="14px"
            fontWeight={600}
            color="textSecondary"
            align="left"
          >
            Coefficient encrypts all of your credentials end-to-end, including Auth tokens, API
            Keys, and passwords.
          </Typography>
        </SheetsFooter>
      </SheetsContent>
    </SheetsModal>
  ) : (
    <Modal {...rest}>
      <DriveContent>
        <IconRow>
          <ConnectPlugIcon height="60" />
          <GoogleDrive width="60" height="60" />
        </IconRow>

        <ContentText>
          <Typography fontWeight="bold" variant="h6">
            Let's connect to Google Drive
          </Typography>

          <Typography color="textSecondary">
            Authorize Coefficient to
            <br />
            connect to Google Drive
          </Typography>
        </ContentText>

        <Button disabled={isLoading} block type="primary" onClick={() => handleAuthorizeClick()}>
          Authorize
        </Button>

        <div style={{ display: "flex", alignItems: "center", gap: 8, lineHeight: 1.3 }}>
          <Lock fill={COLORS.black} />

          <Typography variant="body3" color="textSecondary">
            Coefficient encrypts all of your credentials end-to-end,
            <br /> including OAuth tokens, API Keys, and passwords.
          </Typography>
        </div>
      </DriveContent>
    </Modal>
  );
};

export const GoogleAuthModal = connect(({ app }: AppState): ConnectProps => ({ email: app.email }))(
  GoogleAuthModalBase
);
